import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { 
  Shield, 
  CheckCircle, 
  Star, 
  Clock, 
  Wrench, 
  Users,
  ChevronRight,
  Menu,
  X,
  ArrowRight,
  BadgeCheck,
  FileSearch,
  Award,
  Lock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Fetch real stats from the database
      const [mechanicsRes] = await Promise.all([
        axios.get(`${API_URL}/api/mechanics`)
      ]);
      setStats({
        totalMechanics: mechanicsRes.data.length,
        avgRating: mechanicsRes.data.length > 0 
          ? (mechanicsRes.data.reduce((sum, m) => sum + (m.rating || 0), 0) / mechanicsRes.data.length).toFixed(1)
          : 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleGetStarted = () => {
    if (user) {
      if (user.role === 'admin') navigate('/admin');
      else if (user.role === 'mechanic') navigate('/mechanic');
      else navigate('/dashboard');
    } else {
      navigate('/register');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img src="/logo.png" alt="Mechanicease" className="h-14 w-auto" />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              <Link to="/mechanics" className="text-slate-600 hover:text-slate-900 font-medium text-sm" data-testid="nav-mechanics">
                Find Mechanics
              </Link>
              <Link to="/apply" className="text-slate-600 hover:text-slate-900 font-medium text-sm" data-testid="nav-apply">
                Partner Program
              </Link>
              {user ? (
                <>
                  <Button variant="ghost" className="text-sm" onClick={() => navigate(user.role === 'admin' ? '/admin' : user.role === 'mechanic' ? '/mechanic' : '/dashboard')} data-testid="nav-dashboard">
                    Dashboard
                  </Button>
                  <Button variant="outline" className="text-sm" onClick={logout} data-testid="nav-logout">
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" className="text-sm" onClick={() => navigate('/login')} data-testid="nav-login">
                    Sign In
                  </Button>
                  <Button className="bg-slate-900 hover:bg-slate-800 text-white text-sm px-5" onClick={() => navigate('/register')} data-testid="nav-register">
                    Get Started
                  </Button>
                </>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 py-4">
            <div className="max-w-7xl mx-auto px-4 flex flex-col gap-3">
              <Link to="/mechanics" className="text-slate-600 py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>Find Mechanics</Link>
              <Link to="/apply" className="text-slate-600 py-2 font-medium" onClick={() => setMobileMenuOpen(false)}>Partner Program</Link>
              {user ? (
                <>
                  <Button variant="ghost" className="justify-start" onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}>Dashboard</Button>
                  <Button variant="outline" onClick={() => { logout(); setMobileMenuOpen(false); }}>Logout</Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" className="justify-start" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>Sign In</Button>
                  <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={() => { navigate('/register'); setMobileMenuOpen(false); }}>Get Started</Button>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-50"></div>
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-100 rounded-full blur-3xl opacity-30"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-slate-200 rounded-full blur-3xl opacity-40"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-1.5 mb-6 shadow-sm">
              <BadgeCheck className="w-4 h-4 text-green-600" />
              <span className="text-slate-700 text-sm font-medium">Every mechanic is personally vetted</span>
            </div>
            <h1 className="font-['Barlow'] text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 mb-6 leading-[1.1] tracking-tight" data-testid="hero-title">
              Auto Repair You Can<br />
              <span className="text-orange-500">Actually Trust</span>
            </h1>
            <p className="text-slate-600 text-lg md:text-xl mb-8 max-w-xl leading-relaxed" data-testid="hero-subtitle">
              Stop gambling on mechanics. Every professional on Mechanicease has been verified for credentials, reviewed for quality, and committed to honest service.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                size="lg" 
                className="bg-slate-900 hover:bg-slate-800 text-white font-semibold px-8 h-12 rounded-lg shadow-lg shadow-slate-900/10"
                onClick={handleGetStarted}
                data-testid="hero-cta-primary"
              >
                Find a Mechanic <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-slate-300 text-slate-700 hover:bg-slate-100 h-12 rounded-lg"
                onClick={() => navigate('/mechanics')}
                data-testid="hero-cta-secondary"
              >
                Browse Mechanics
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar intentionally hidden during recruitment phase —
          re-enable when real mechanic count and ratings are strong. */}

      {/* Problem/Solution */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Barlow'] text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-testid="section-title">
              The Auto Repair Industry Has a Trust Problem
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              We built Mechanicease to fix it.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            {/* Problems Column */}
            <div className="bg-slate-900 rounded-2xl p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <X className="w-5 h-5 text-red-400" />
                </div>
                <h3 className="font-['Barlow'] text-xl font-bold text-white">What You're Used To</h3>
              </div>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-1 bg-red-500/50 rounded-full flex-shrink-0"></div>
                  <div>
                    <p className="font-semibold text-white mb-1">Overpriced Repairs</p>
                    <p className="text-slate-400 text-sm">Hidden fees and inflated labor costs that you only discover at checkout.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-1 bg-red-500/50 rounded-full flex-shrink-0"></div>
                  <div>
                    <p className="font-semibold text-white mb-1">Unnecessary Work</p>
                    <p className="text-slate-400 text-sm">Recommendations for repairs your car doesn't actually need.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-1 bg-red-500/50 rounded-full flex-shrink-0"></div>
                  <div>
                    <p className="font-semibold text-white mb-1">Endless Delays</p>
                    <p className="text-slate-400 text-sm">"One more day" turns into a week. No updates, no communication, no respect for your time.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-1 bg-red-500/50 rounded-full flex-shrink-0"></div>
                  <div>
                    <p className="font-semibold text-white mb-1">Zero Accountability</p>
                    <p className="text-slate-400 text-sm">Bad mechanics face no consequences. They just keep operating.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Solutions Column */}
            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-['Barlow'] text-xl font-bold text-white">What Mechanicease Delivers</h3>
              </div>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-1 bg-white/50 rounded-full flex-shrink-0"></div>
                  <div>
                    <p className="font-semibold text-white mb-1">Transparent Pricing</p>
                    <p className="text-orange-100 text-sm">Every invoice shows labor hours and parts separately. No surprises.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-1 bg-white/50 rounded-full flex-shrink-0"></div>
                  <div>
                    <p className="font-semibold text-white mb-1">Only What You Need</p>
                    <p className="text-orange-100 text-sm">Our mechanics are vetted and committed to honest recommendations.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-1 bg-white/50 rounded-full flex-shrink-0"></div>
                  <div>
                    <p className="font-semibold text-white mb-1">No Unnecessary Delays</p>
                    <p className="text-orange-100 text-sm">Clear timelines, proactive updates, and respect for your schedule. Your time matters.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-1 bg-white/50 rounded-full flex-shrink-0"></div>
                  <div>
                    <p className="font-semibold text-white mb-1">Performance Tracked</p>
                    <p className="text-orange-100 text-sm">On-time rates, response times, and satisfaction scores visible for every mechanic.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-orange-600 font-semibold text-sm tracking-wide uppercase mb-3">Full-Service Marketplace</p>
            <h2 className="font-['Barlow'] text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-testid="how-it-works-title">
              More Than Just a Directory
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              We manage the entire process from booking to payment, ensuring accountability at every step.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="relative" data-testid="step-1">
              <div className="bg-white rounded-xl p-8 border border-slate-200 shadow-sm h-full">
                <div className="w-12 h-12 bg-slate-900 rounded-lg flex items-center justify-center mb-6">
                  <span className="font-['Barlow'] text-xl font-bold text-orange-500">01</span>
                </div>
                <h3 className="font-['Barlow'] text-xl font-bold text-slate-900 mb-3">Book Through Us</h3>
                <p className="text-slate-600 leading-relaxed">Find a verified mechanic, describe your issue, and book online. We track every booking for your protection.</p>
              </div>
            </div>

            <div className="relative" data-testid="step-2">
              <div className="bg-white rounded-xl p-8 border border-slate-200 shadow-sm h-full">
                <div className="w-12 h-12 bg-slate-900 rounded-lg flex items-center justify-center mb-6">
                  <span className="font-['Barlow'] text-xl font-bold text-orange-500">02</span>
                </div>
                <h3 className="font-['Barlow'] text-xl font-bold text-slate-900 mb-3">Transparent Invoicing</h3>
                <p className="text-slate-600 leading-relaxed">Receive itemized invoices showing labor hours, parts costs, and receipts. No hidden fees, no surprises.</p>
              </div>
            </div>

            <div className="relative" data-testid="step-3">
              <div className="bg-white rounded-xl p-8 border border-slate-200 shadow-sm h-full">
                <div className="w-12 h-12 bg-slate-900 rounded-lg flex items-center justify-center mb-6">
                  <span className="font-['Barlow'] text-xl font-bold text-orange-500">03</span>
                </div>
                <h3 className="font-['Barlow'] text-xl font-bold text-slate-900 mb-3">Secure Payment</h3>
                <p className="text-slate-600 leading-relaxed">Pay securely through the platform. Your payment history is tracked for dispute protection if ever needed.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vetting Process */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-orange-600 font-semibold text-sm tracking-wide uppercase mb-3">Our Standards</p>
              <h2 className="font-['Barlow'] text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                We Vet Every Mechanic
              </h2>
              <p className="text-slate-600 text-lg mb-8 leading-relaxed">
                Not every mechanic qualifies for our network. We maintain strict standards to ensure every professional on our platform is trustworthy.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileSearch className="w-5 h-5 text-slate-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">License Verification</p>
                    <p className="text-sm text-slate-600">We verify every business license with issuing authorities</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-slate-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Background Check</p>
                    <p className="text-sm text-slate-600">Clean business record required</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-slate-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Experience Required</p>
                    <p className="text-sm text-slate-600">Minimum years of professional experience</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Lock className="w-5 h-5 text-slate-700" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Code of Ethics</p>
                    <p className="text-sm text-slate-600">Commitment to honest, transparent service</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">No Unnecessary Delays</p>
                    <p className="text-sm text-slate-600">Timely repairs with clear communication and updates</p>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <Button 
                  variant="outline" 
                  className="border-slate-300 text-slate-700 hover:bg-slate-100"
                  onClick={() => navigate('/vetting')}
                  data-testid="learn-vetting-btn"
                >
                  Learn About Our Vetting Process <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="bg-slate-900 rounded-2xl p-8 md:p-10">
              <div className="text-center mb-8">
                <p className="text-orange-500 font-semibold text-sm tracking-wide uppercase mb-2">For Mechanics</p>
                <h3 className="font-['Barlow'] text-2xl md:text-3xl font-bold text-white mb-3">
                  Join Our Network
                </h3>
                <p className="text-slate-400">
                  Are you a qualified mechanic committed to honest service? Apply to join our verified network.
                </p>
              </div>
              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                  <span>Connect with customers who value quality</span>
                </div>
                <div className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                  <span>Verified trust badge on your profile</span>
                </div>
                <div className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                  <span>Built-in booking and invoicing tools</span>
                </div>
                <div className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                  <span>Secure online payments</span>
                </div>
              </div>
              <Button 
                className="w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg"
                onClick={() => navigate('/apply')}
                data-testid="mechanic-apply-btn"
              >
                Apply to Join <ChevronRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Features */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-orange-600 font-semibold text-sm tracking-wide uppercase mb-3">Why Choose Us</p>
            <h2 className="font-['Barlow'] text-3xl md:text-4xl font-bold text-slate-900" data-testid="trust-title">
              Built for Trust
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 border border-slate-200 shadow-sm" data-testid="trust-1">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-slate-900" />
              </div>
              <h3 className="font-['Barlow'] text-xl font-bold text-slate-900 mb-3">Verified Mechanics Only</h3>
              <p className="text-slate-600 leading-relaxed">Every mechanic on our platform has passed our vetting process. No exceptions.</p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-slate-200 shadow-sm" data-testid="trust-2">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-6">
                <Star className="w-7 h-7 text-slate-900" />
              </div>
              <h3 className="font-['Barlow'] text-xl font-bold text-slate-900 mb-3">Real Reviews Only</h3>
              <p className="text-slate-600 leading-relaxed">All reviews come from verified customers with completed bookings.</p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-slate-200 shadow-sm" data-testid="trust-3">
              <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-6">
                <Clock className="w-7 h-7 text-slate-900" />
              </div>
              <h3 className="font-['Barlow'] text-xl font-bold text-slate-900 mb-3">Transparent Invoices</h3>
              <p className="text-slate-600 leading-relaxed">Every invoice shows labor hours and parts costs separately. You see exactly what you're paying for.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 bg-slate-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Barlow'] text-3xl md:text-4xl font-bold text-white mb-6 leading-tight" data-testid="final-cta-title">
            Ready to Find a Mechanic<br />You Can Trust?
          </h2>
          <p className="text-slate-400 mb-4 text-lg">
            Join car owners who have found reliable, honest mechanics through Mechanicease.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 px-4 py-2">
              No Unnecessary Delays
            </Badge>
            <Badge className="bg-green-500/20 text-green-400 border-green-500/30 px-4 py-2">
              No Surprises
            </Badge>
            <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 px-4 py-2">
              No Hidden Fees
            </Badge>
          </div>
          <Button 
            size="lg"
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-10 h-12 rounded-lg shadow-lg"
            onClick={handleGetStarted}
            data-testid="final-cta-btn"
          >
            Get Started <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <Link to="/" className="flex items-center gap-2 mb-4">
                <img src="/logo.png" alt="Mechanicease" className="h-10 w-auto" />
              </Link>
              <p className="text-slate-400 text-sm">
                Trusted auto repair marketplace connecting car owners with verified, honest mechanics.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Platform</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/mechanics" className="hover:text-white transition-colors">Find Mechanics</Link></li>
                <li><Link to="/apply" className="hover:text-white transition-colors">Partner Program</Link></li>
                <li><Link to="/vetting" className="hover:text-white transition-colors">Our Vetting Process</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/policies/terms" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/policies/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/policies/refund" className="hover:text-white transition-colors">Refund Policy</Link></li>
                <li><Link to="/policies" className="hover:text-white transition-colors">All Policies</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/careers" className="hover:text-white transition-colors">Careers</Link></li>
                <li><a href="mailto:support@mechanicease.ca" className="hover:text-white transition-colors">support@mechanicease.ca</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-600 text-sm">© 2024 Mechanicease. All rights reserved.</p>
            <p className="text-slate-600 text-sm">No Unnecessary Delays. No Surprises. No Hidden Fees.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
